import { useController } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { maskPhone } from '@givebutter/util';
import { faEnvelope, faPhone } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateAccount } from 'api/account';
import * as Form from 'components/form';
import * as SettingsCard from 'components/settings-card';
import { TimezoneSelect } from 'components/timezone-select';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useInvalidateUserAndAccount } from 'customHooks/useInvalidateUserAndAccount';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { Account } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface GeneralSettingsFormValues {
    accountName: string;
    tagline: string;
    timezone: string;
    email: string;
    phone: string;
}

const GeneralSettings = () => {
    const toast = useToast();
    const { accountId, name, tagline, timezone, email, phone } = useCurrentAccount();
    const { mutate: invalidateUserData } = useInvalidateUserAndAccount();

    const methods = Form.useGivebutterForm<GeneralSettingsFormValues>({
        defaultValues: {
            accountName: name || '',
            tagline: tagline || '',
            timezone: timezone || DateTime.now().zoneName,
            email: email || '',
            phone: maskPhone(phone ?? ''),
        },
    });
    const { control, handleSubmit, path, setError, resetFormButton } = methods;
    const { field } = useController({ control, name: 'timezone' });

    const { mutate: submit, isPending: isLoading } = useMutation<
        Account,
        GivebutterAxiosError,
        GeneralSettingsFormValues
    >({
        mutationFn: values => updateAccount(accountId, transformValuesToRequest(values)),
        onSuccess: () => {
            toast.success('Updated General Details!');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.current(accountId) });
            invalidateUserData();
        },
        onError: (e: GivebutterAxiosError) => {
            const data = e.response?.data;
            const errors = data?.errors;

            toast.error(data?.message ?? 'Failed to update General Details');

            if (typeof errors === 'object') {
                for (let field in errors) {
                    setError(field as keyof GeneralSettingsFormValues, {
                        message: errors[field][0],
                    });
                }
            }
            resetFormButton();
        },
    });

    return (
        <SettingsCard.Root>
            <SettingsCard.LeftSide>
                <SettingsCard.Heading>General Details</SettingsCard.Heading>
            </SettingsCard.LeftSide>
            <SettingsCard.RightSide>
                <Form.Provider {...methods}>
                    <form
                        onSubmit={handleSubmit(values => submit(values))}
                        className="flex flex-col gap-4"
                    >
                        <Form.Container>
                            <Form.Label className="text-base" name={path('accountName')}>
                                Account name
                                <Form.LabelAsterisk />
                            </Form.Label>
                            <Form.TextInputRoot
                                control={control}
                                name={path('accountName')}
                                rules={{ required: 'Please enter an account name.' }}
                            >
                                <Form.TextInputForm autoComplete="off" />
                            </Form.TextInputRoot>
                            <Form.Error name={path('accountName')} />
                        </Form.Container>

                        <Form.Container>
                            <Form.Label className="text-base" name={path('tagline')}>
                                Tagline
                            </Form.Label>
                            <Form.UseFormTextInputRoot control={control} name="tagline">
                                <Form.TextInputForm />
                            </Form.UseFormTextInputRoot>
                            <Form.Error name={path('tagline')} />
                        </Form.Container>

                        <Form.Container>
                            <Form.Label className="text-base" name={path('timezone')}>
                                Time zone
                            </Form.Label>
                            <TimezoneSelect value={field.value} onChange={field.onChange} />
                            <Form.Error name={path('timezone')} />
                        </Form.Container>

                        <Form.Container>
                            <Form.Label className="text-base" name={path('email')}>
                                Email address
                            </Form.Label>
                            <Form.UseFormTextInputRoot control={control} name="email">
                                <FontAwesomeIcon icon={faEnvelope} />
                                <Form.TextInputForm placeholder="Enter email address" />
                            </Form.UseFormTextInputRoot>
                            <Form.Error name={path('email')} />
                        </Form.Container>

                        <Form.Container>
                            <Form.Label className="text-base" name={path('phone')}>
                                Phone number
                            </Form.Label>
                            <Form.TextInputRoot
                                control={control}
                                name="phone"
                                inputRootProps={{
                                    mask: 'phone',
                                }}
                            >
                                <FontAwesomeIcon icon={faPhone} />
                                <Form.TextInputForm placeholder="Enter phone number" />
                            </Form.TextInputRoot>
                            <Form.Error name={path('phone')} />
                        </Form.Container>

                        <Form.Container className="flex-row">
                            <Form.SubmitButton isSubmitting={isLoading}>Save</Form.SubmitButton>
                        </Form.Container>
                    </form>
                </Form.Provider>
            </SettingsCard.RightSide>
        </SettingsCard.Root>
    );
};

const transformValuesToRequest = (values: GeneralSettingsFormValues) => {
    return {
        name: values.accountName,
        tagline: values.tagline,
        timezone: values.timezone,
        email: values.email,
        phone: values.phone,
    };
};

export { GeneralSettings };
